/**
 * KB-1515 — shared run-artifact fetch helpers for the reviewer blocker-resolution modals.
 *
 * The per-run audit artifacts (match_report.json, observed_sessions.jsonl, …) live in GCS under
 * `_ai_parse_audit/<campSlug>/runs/<runId>/` and are fetched via the `getCampDataArtifactUrl`
 * callable (a signed view URL). Both the disambiguate-variation modal (AmbiguousVariationModal) and
 * the location/program blocker-resolution modals read them, so the fetch + parse logic lives here
 * once instead of being duplicated per component.
 *
 * Every fetch DEGRADES GRACEFULLY: a missing artifact (recent runs don't upload them — KB-1516)
 * yields null/[] with an explicit `present` flag, never a throw. Callers distinguish
 * "artifact absent" from "present but empty" to render the right empty-state copy.
 *
 * KB-3290 round 1 (#1): both fetchers now delegate to the shared classified fetcher
 * (`@/services/campDataArtifactFetch`) instead of hand-rolling their own "return null/[] on ANY
 * failure" logic. `present` stays for existing callers that only need the coarse "couldn't load"
 * signal; the new `outcome` field lets a caller additionally tell a confirmed-absent artifact
 * (nothing was ever uploaded) apart from a fetch that genuinely FAILED (network/HTTP/parse/auth
 * error — we don't actually know what's there). Before this, `present:false` meant both, and at
 * least one consumer (AmbiguousVariationModal) rendered a confident, specific, WRONG claim for the
 * failed-fetch case ("the match report wasn't uploaded — this is an older run. Re-run the camp to
 * disambiguate its programs.") when the real cause was a transient fetch error — the same
 * calm-wrong-answer defect this file's parent ticket exists to close, one layer removed.
 */

import {
  fetchJsonArtifactAtRelPath,
  fetchJsonlArtifactAtRelPath,
  type ArtifactFetchOutcome,
} from "@/services/campDataArtifactFetch";

/** Build the GCS-relative path for a per-run audit artifact. */
export function runArtifactRelPath(campSlug: string, runId: string, file: string): string {
  return `_ai_parse_audit/${campSlug}/runs/${runId}/${file}`;
}

/** A fetched JSON artifact + whether it was actually present (vs absent → degrade-gracefully). */
export interface JsonArtifactResult<T> {
  /** Parsed JSON, or null when the artifact is absent / unparseable / unfetchable. */
  data: T | null;
  /** True iff the artifact was fetched and parsed successfully (`outcome === "ok"`). */
  present: boolean;
  /**
   * KB-3290 — the fine-grained outcome `present` collapses: `"absent"` (confirmed — nothing was
   * ever uploaded) vs `"error"` (fetch/parse/auth failure — unknown whether the artifact exists).
   * Callers that render a specific causal claim for a non-present result MUST branch on this
   * rather than assuming `!present` always means "confirmed absent".
   */
  outcome: ArtifactFetchOutcome;
}

/**
 * Fetch + parse a JSON run artifact. Returns `{ data: null, present: false, outcome }` when the
 * artifact is absent or unfetchable — callers use `present` to tell "not uploaded for this run"
 * apart from a genuinely-empty result, and `outcome` to further tell a confirmed absent apart from
 * a failed fetch (KB-3290).
 */
export async function fetchJsonArtifact<T>(campSlug: string, runId: string, file: string): Promise<JsonArtifactResult<T>> {
  const r = await fetchJsonArtifactAtRelPath<T>(campSlug, runId, runArtifactRelPath(campSlug, runId, file));
  return { data: r.data, present: r.outcome === "ok", outcome: r.outcome };
}

/** A fetched JSONL artifact's rows + whether it was actually present (mirrors {@link JsonArtifactResult}). */
export interface JsonlArtifactResult {
  /** Parsed rows; `[]` when the artifact is absent / unparseable / unfetchable. */
  rows: Array<Record<string, any>>;
  /** True iff the artifact was fetched and parsed successfully (`outcome === "ok"`). */
  present: boolean;
  /**
   * KB-3290 round 2 (#1): the shared fetcher already classifies "absent" vs "error" for JSONL
   * artifacts — this wrapper used to throw that classification away and return only `rows`, so
   * every caller re-introduced the exact defect this ticket exists to close, one layer up (a
   * confirmed-absent artifact and a FAILED fetch both silently became `[]`). Mirrors
   * {@link JsonArtifactResult.outcome}.
   */
  outcome: ArtifactFetchOutcome;
}

/** Fetch + parse a JSONL run artifact into rows, classified the same way as {@link fetchJsonArtifact}. */
export async function fetchJsonlArtifact(campSlug: string, runId: string, file: string): Promise<JsonlArtifactResult> {
  const r = await fetchJsonlArtifactAtRelPath(campSlug, runId, runArtifactRelPath(campSlug, runId, file));
  return { rows: r.rows, present: r.outcome === "ok", outcome: r.outcome };
}
